import { evidenceDefinitionContract } from "@clipboard-health/contract-documents-service";
import { useGetParsedQuery } from "@src/appV2/api/useGetParsedQuery";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";

import { type EvidenceDefinition } from "../types";

const SEARCH_PAGE_SIZE = 20;
const PREFETCH_PAGE_SIZE = 10;

/** The `{ id, displayName }` shape the picker components render as options. */
export interface DefinitionOption {
  id: string;
  displayName: string;
}

export function toDefinitionOption(definition: EvidenceDefinition): DefinitionOption {
  return { id: definition.id, displayName: definition.attributes.displayName };
}

interface UseSearchEvidenceDefinitionsParams {
  search: string;
  /**
   * When true, fetch a default first page (no `displayName` filter) while the
   * search box is empty, so the picker shows some definitions before the user
   * types. The backend `list` endpoint returns an unfiltered, cursor-paginated
   * page (ordered by `createdAt`) when no filter is supplied — this fetches just
   * the first {@link PREFETCH_PAGE_SIZE}, not the eager full-catalog pagination
   * that ACT-4987 removed.
   */
  prefetchOnEmpty?: boolean;
}

interface UseSearchEvidenceDefinitionsResult {
  data: EvidenceDefinition[];
  isFetching: boolean;
}

/**
 * Server-side search over evidence definitions, filtered by `displayName`.
 *
 * Returns the full `EvidenceDefinition[]` (including `attributes.propertySchema`)
 * so a newly-selected definition can supply its own property schema with no extra
 * fetch. Callers that only need `{ id, displayName }` options map at the call site.
 */
export function useSearchEvidenceDefinitions({
  search,
  prefetchOnEmpty = false,
}: UseSearchEvidenceDefinitionsParams): UseSearchEvidenceDefinitionsResult {
  const baseUrl = environmentConfig.REACT_APP_DOCUMENTS_SERVICE_REST_API_URL;
  const hasSearch = search.length > 0;

  const query = useGetParsedQuery({
    url: `${baseUrl}/v2/evidence-definitions`,
    queryParams: hasSearch
      ? {
          page: { size: SEARCH_PAGE_SIZE },
          filter: { displayName: { search: [search] } },
        }
      : { page: { size: PREFETCH_PAGE_SIZE } },
    queryParamsSchema: evidenceDefinitionContract.list.query,
    responseSchema: evidenceDefinitionContract.list.responses[200],
    enabled: hasSearch || prefetchOnEmpty,
    keepPreviousData: true,
    meta: { logErrorMessage: APP_V2_APP_EVENTS.SEARCH_V2_EVIDENCE_DEFINITIONS_FAILURE },
  });

  return {
    data: query.data?.data ?? [],
    isFetching: query.isFetching,
  };
}
